import _ from 'lodash';
import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  Icon,
  Link,
  FormLayout,
  SkeletonDisplayText,
  SkeletonBodyText,
  Badge
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { change, Field, FieldArray, getFormValues, reset } from 'redux-form';
import { CirclePlusMinor, ExternalSmallMinor } from '@shopify/polaris-icons';
import { getUTCDate } from 'src/helpers/date-time-helper';

import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderQueryField from 'src/components/views/merchandising-v2/rule/conditions/render-queries-field.jsx';
import RenderDatePicker from 'src/components/views/merchandising-v2/rule/conditions/render-date-picker';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import ConfirmModal from 'src/components/views/common/confirm-modal';
import { isRequired, hasTag, isExceedLength } from 'src/helpers/form-validation-helper';
import { removeEmptyStrategy, hasProductInRule } from 'src/helpers/merchandising-v2-helper';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { NUMBER_CONDITION_PER_TYPE } from 'src/constants/merchandising-v2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import * as MerchandisingV2Actions from 'actions/merchandising-v2-actions';
import CrownPremium from 'src/assets/images/common/crown-red.png';
import { routes } from 'src/routes';

const isExceedLengthValue = isExceedLength(150);

type Props = {
  loading: boolean,
  appLanguage: string,
  updateField: () => void
};
type State = {
  activePopoverDate: boolean,
  enabledFromDate: string,
  enabledToDate: string,
  collectionBeforeConfirm: any,
  queriesTypeBeforeConfirm: string,
  changePlanFromReviewIOModal: boolean
};
type ComposedProps = Props & WithI18nProps;
class Condition extends Component<ComposedProps, State> {
  state = {
    activePopoverDate: false,
    confirmModal: false,
    enabledFromDate: '',
    enabledToDate: '',
    collectionBeforeConfirm: {},
    queriesTypeBeforeConfirm: '',
    changePlanFromReviewIOModal: false
  };

  isRequiredValue = isRequired(
    this.props.i18n.translate('AdminHTML.MerchandisingV2.Helper.validateBlankValue')
  );

  componentDidUpdate(prevProps) {
    const { formValues: prevFormValues } = prevProps;
    const { formValues } = this.props;
    const { effectFrom = null, effectTo = null } = formValues;
    if (formValues !== prevFormValues) {
      this.setState({ enabledFromDate: effectFrom, enabledToDate: effectTo });
    }
  }

  renderQueries() {
    const {
      i18n,
      formValues,
      merchandisingRules,
      ruleId,
      existUniversalRule,
      checkDuplicateTerm,
      duplicateTerm
    } = this.props;
    const {
      queriesType = 'specific',
      isUniversal = false,
      queryCondition = 'are',
      queries,
      isAppliedSingularAndPlurals
    } = formValues;

    const conductorOptions = [
      {
        value: 'are',
        label: i18n.translate('AdminHTML.MerchandisingV2.Conductor.are')
      },
      {
        value: 'contain',
        label: i18n.translate('AdminHTML.MerchandisingV2.Conductor.contain')
      }
    ];

    const queryOptions = [
      {
        value: 'specific',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.specificQueries')
      }
    ];

    if (existUniversalRule && !isUniversal) {
      queryOptions.push({
        disabled: true,
        value: 'all',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueriesExists')
      });
    } else {
      queryOptions.push({
        value: 'all',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueries')
      });
    }

    const selectField = (
      <Field
        name="queriesType"
        formValues={formValues}
        merchandisingRules={merchandisingRules}
        ruleId={ruleId}
        options={queryOptions}
        component={RenderSelect}
        label={i18n.translate('AdminHTML.MerchandisingV2.Common.applyTo')}
      />
    );

    if (queriesType === 'specific') {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <div style={{ width: '70%', paddingRight: 10 }}>{selectField}</div>
            <div style={{ width: '30%' }}>
              <Field
                name="queryCondition"
                formValues={formValues}
                options={conductorOptions}
                component={RenderSelect}
              />
            </div>
          </div>
          <div>
            <div className="wrap-queries">
              <div className="wrap-input">
                <FieldArray
                  name="queries"
                  formValues={formValues}
                  merchandisingRules={merchandisingRules}
                  checkDuplicateTerm={checkDuplicateTerm}
                  isAppliedSingularAndPlurals={isAppliedSingularAndPlurals}
                  duplicateTerm={duplicateTerm}
                  ruleId={ruleId}
                  conductor={queryCondition}
                  component={RenderQueryField}
                  prefix={i18n.translate('AdminHTML.MerchandisingV2.Rule.card1_Field3_SubLabel')}
                  placeholder={i18n.translate(
                    'AdminHTML.MerchandisingV2.Rule.card1_Field3_Placeholder'
                  )}
                />
              </div>
            </div>
            {queries.length < 20 && (
              <div className="mt-3 apply-same-rule-queries">
                <p style={{ maxWidth: 'fit-content' }} onClick={() => this.handleAddQueryOption()}>
                  <Icon source={CirclePlusMinor} color="base" />{' '}
                  <span> {i18n.translate('AdminHTML.MerchandisingV2.Rule.addQueries')}</span>
                </p>
              </div>
            )}
            <div style={{ marginTop: '16px' }}>
              <Field
                name="isAppliedSingularAndPlurals"
                component={RenderCheckbox}
                label={i18n.translate('AdminHTML.MerchandisingV2.Improvement.applyStemmer')}
              />
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <Field
          name="queriesType"
          formValues={formValues}
          merchandisingRules={merchandisingRules}
          ruleId={ruleId}
          options={queryOptions}
          component={RenderSelect}
          label={i18n.translate('AdminHTML.MerchandisingV2.Common.applyTo')}
        />
        <div className="all-rule-note">
          <span> {i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueriesNote')}</span>
        </div>
      </>
    );
  }

  renderCollections() {
    const {
      i18n,
      loadingFilter,
      formValues,
      collections,
      merchandisingRules,
      ruleId,
      existUniversalRule
    } = this.props;
    const { queriesType = 'specific', isUniversal = false } = formValues;

    const selectOptions = [
      {
        value: 'specific',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.specificCollect')
      }
    ];

    if (existUniversalRule && !isUniversal) {
      selectOptions.push({
        disabled: true,
        value: 'all',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueriesExists')
      });
    } else {
      selectOptions.push({
        value: 'all',
        label: i18n.translate('AdminHTML.MerchandisingV2.Rule.allCollect')
      });
    }

    const validCollections = this.removeExcludedValues(collections);

    return (
      <>
        <Field
          name="queriesType"
          formValues={formValues}
          merchandisingRules={merchandisingRules}
          ruleId={ruleId}
          options={selectOptions}
          onChange={() => this.handleChangeCollection()}
          component={RenderSelect}
          label={i18n.translate('AdminHTML.MerchandisingV2.Common.applyTo')}
        />
        {queriesType === 'specific' ? (
          <div>
            <div className="wrap-queries">
              <div className="wrap-select">
                <div className="border-connect-select"></div>
                <Field
                  name={'collection'}
                  onChange={() => this.handleChangeCollection()}
                  loading={loadingFilter}
                  component={RenderSelect2}
                  options={validCollections}
                  placeholder={i18n.translate(
                    'AdminHTML.MerchandisingV2.Condition.collection_placeholder'
                  )}
                  validate={[this.isRequiredValue]}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="all-rule-note">
            <span> {i18n.translate('AdminHTML.MerchandisingV2.Rule.allCollectNote')}</span>
          </div>
        )}
      </>
    );
  }

  render() {
    const { activePopoverDate, enabledFromDate, enabledToDate, confirmModal } = this.state;

    const {
      i18n,
      loading,
      appLanguage,
      formValues,
      ruleScheduling,
      isSubscriptionV4,
      enableVSAddon
    } = this.props;
    const { ruleType = 'search' } = formValues;

    const isRuleScheduling = ruleScheduling || !isSubscriptionV4;
    return (
      <div className="set-rule-conditions">
        {loading ? (
          <FormLayout>
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={1} />
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={1} />
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={1} />
          </FormLayout>
        ) : (
          <FormLayout>
            <Field
              name="name"
              component={RenderTextField}
              label={i18n.translate('AdminHTML.MerchandisingV2.Common.ruleName')}
              placeholder={i18n.translate(
                'AdminHTML.MerchandisingV2.Rule.card1_Field1_Placeholder'
              )}
              validate={[this.isRequiredValue, hasTag, isExceedLengthValue]}
            />
            <RenderDatePicker
              name="dateRange"
              stylePlaceholder="BC-Page__Merchandising-Rule-DateRange"
              label={
                <div className="premium-badge-crown">
                  {i18n.translate('AdminHTML.MerchandisingV2.Common.dateRange')}{' '}
                  {!isRuleScheduling && (
                    <Badge status="warning">
                      <img alt="crown" src={CrownPremium} width={11} height={8} />
                      <span className="title">
                        {i18n.translate('AdminHTML.Common.Badge.premium')}
                      </span>
                    </Badge>
                  )}
                </div>
              }
              placeholder={i18n.translate(
                'AdminHTML.MerchandisingV2.Rule.card1_Field2_Placeholder'
              )}
              disabled={!isRuleScheduling && !enableVSAddon}
              onChooseDate={this.onChooseDate}
              enabledFromDate={enabledFromDate}
              enabledToDate={enabledToDate}
              appLanguage={appLanguage}
              popoverActive={activePopoverDate}
              togglePopoverDateActive={() => this.handleTogglePopover('activePopoverDate')}
            />
            {!isRuleScheduling && (
              <span className="upgrade-plan-text">
                {i18n.translate(`AdminHTML.MerchandisingV2.Plan.upgradeInRule`, {
                  t: (
                    <Link onClick={this.openPackagePage}>
                      {i18n.translate('AdminHTML.MerchandisingV2.Plan.upgradeInRule_t')}
                      <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
                    </Link>
                  )
                })}
              </span>
            )}
            {ruleType === 'search' ? this.renderQueries() : this.renderCollections()}
          </FormLayout>
        )}
        <ConfirmModal
          active={confirmModal}
          handleCancel={this.handleCancelConfirm}
          handleConfirm={this.handleConfirm}
          title={i18n.translate('AdminHTML.MerchandisingV2.ConfirmChangeCollection.title')}
          content={i18n.translate('AdminHTML.MerchandisingV2.ConfirmChangeCollection.content')}
        />
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  // Toggle modal
  toggleModal = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  openPackagePage = () => {
    const { history, useBoltOn } = this.props;

    if (useBoltOn) {
      this.setState({ changePlanFromReviewIOModal: true });
      return;
    }

    history.push(routes.subscription.package());
  };

  // Confirm onChange collection and remove all strategies
  handleConfirm = () => {
    const { updateField, handleOpenStrategyCard, formValues } = this.props;
    const { products } = formValues || {};
    updateField('strategies', []);

    const newProducts = {
      ...products
    };
    Object.keys(products).forEach(type => {
      newProducts[type] = Array.from(Array(NUMBER_CONDITION_PER_TYPE[type] || 3).keys()).map(
        _ => ''
      );
    });

    updateField('products', newProducts);
    handleOpenStrategyCard('');
    this.toggleModal('confirmModal');
  };

  handleCancelConfirm = () => {
    const { updateField } = this.props;
    updateField('collection', this.state.collectionBeforeConfirm);
    updateField('queriesType', this.state.queriesTypeBeforeConfirm);
    this.toggleModal('confirmModal');
  };

  handleChangeCollection = () => {
    // Check if has attribute in strategies
    const { formValues } = this.props;
    const { collection, queriesType = 'specific' } = formValues;
    this.setState({ collectionBeforeConfirm: collection, queriesTypeBeforeConfirm: queriesType });
    const { strategies = [], products = {} } = formValues;

    const attributes = removeEmptyStrategy(strategies);
    const hasProduct = hasProductInRule(products);
    if (attributes.length > 0 || hasProduct) {
      this.toggleModal('confirmModal');
    }
  };

  // On choose date
  onChooseDate = selectedDates => {
    const { updateField } = this.props;

    const { start = '', end = '' } = selectedDates;
    this.setState({ enabledFromDate: start, enabledToDate: end });
    const from = moment(start, DATE_FORMAT.en.fullDateNumber);
    const to = moment(end, DATE_FORMAT.en.fullDateNumber);
    if ((start === undefined || !from.isValid()) && (end === undefined || !to.isValid())) {
      updateField('effectFrom', '');
      updateField('effectTo', '');
      return;
    }

    const effected_from = from.toDate();
    const effected_to = to.toDate();
    // Convert from store's time to UTC
    const effected_from_utc = getUTCDate(
      effected_from,
      effected_from.getHours(),
      effected_from.getMinutes(),
      effected_from.getSeconds()
    ).toISOString();

    const effected_to_utc = getUTCDate(
      effected_to,
      effected_to.getHours(),
      effected_to.getMinutes(),
      effected_to.getSeconds()
    ).toISOString();

    updateField('effectFrom', effected_from_utc);
    updateField('effectTo', effected_to_utc);
  };

  // Handle toggle popover active
  handleTogglePopover = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Add query options
  handleAddQueryOption = () => {
    const { formValues, updateField } = this.props;
    const { queries = [] } = formValues;
    let clonedQueries = _.cloneDeep(queries);

    clonedQueries.push('');

    // Update suggestion form
    updateField('queries', clonedQueries);
  };

  // Remove collections is ready exist in other rule
  removeExcludedValues = allOptions => {
    let options = [...allOptions];
    const { merchandisingRules = [], collectionIdUpdating } = this.props;
    if (_.isEmpty(merchandisingRules)) return options;
    const selectedCollection = merchandisingRules
      .filter(item => item.collection)
      .map(item => item.collection.value);
    return options.filter(
      op => op.value === collectionIdUpdating || !selectedCollection.includes(op.value)
    );
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: formName => dispatch(reset(formName)),
  updateField: (field, value) => dispatch(change(`MerchandisingV2RuleForm`, field, value)),
  checkDuplicateTerm: body =>
    dispatch(MerchandisingV2Actions.checkDuplicateTerm(body, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('MerchandisingV2RuleForm')(state);

  return {
    formValues,
    ruleScheduling: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.rule-scheduling',
      true
    ),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    collections: _.get(state.filterData.productAttributes, 'collections', []),
    loadingFilter: _.get(state.filterData, 'loading'),
    loading: _.get(state.merchandisingV2, 'loading', true),
    saving: _.get(state.merchandisingV2, 'saving', false),
    existUniversalRule: _.get(state.merchandisingV2, 'existUniversalRule', false),
    duplicateTerm: _.get(state.merchandisingV2, 'duplicateTerm', {}),
    isSubscriptionV4: _.get(state.oauth.tenantApp, 'settings.subscriptionV4.settings.enable', false)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Condition));
